import { useRouter } from 'next/router';
import React from 'react';

import type { NavItemInternal, NavItem, NavGroupItem } from 'types/client/navigation';

import config from 'configs/app';
import { useTokenStandard } from 'lib/token/useTokenStandard';
import { rightLineArrow } from 'lib/html-entities';

interface ReturnType {
  mainNavItems: Array<NavItem | NavGroupItem>;
  accountNavItems: Array<NavItem>;
}

export function isGroupItem(item: NavItem | NavGroupItem): item is NavGroupItem {
  return 'subItems' in item;
}

export function isInternalItem(item: NavItem): item is NavItemInternal {
  return 'nextRoute' in item;
}

export default function useNavItems(): ReturnType {
  const router = useRouter();
  const pathname = router.pathname;
  const { formatType } = useTokenStandard();
  const tokenStandardLabel = formatType('ERC-20');

  return React.useMemo(() => {
    let blockchainNavItems: Array<NavItem> | Array<Array<NavItem>> = [];

    const topAccounts: NavItem | null = !config.UI.views.address.hiddenViews?.top_accounts ? {
      text: 'Top accounts',
      nextRoute: { pathname: '/accounts' as const },
      icon: 'top-accounts',
      isActive: pathname === '/accounts',
    } : null;
    const blocks: NavItem | null = {
      text: 'Blocks',
      nextRoute: { pathname: '/blocks' as const },
      icon: 'block',
      isActive: pathname === '/blocks' || pathname === '/block/[height_or_hash]',
    };
    const forkedBlocks: NavItem | null = {
      text: 'Forked Blocks (Reorgs)',
      nextRoute: { pathname: '/forked-blocks' as const },
      icon: 'block',
      isActive: pathname === '/forked-blocks',
    };
    const uncleBlocks: NavItem | null = {
      text: 'Uncles',
      nextRoute: { pathname: '/uncle-blocks' as const },
      icon: 'block',
      isActive: pathname === '/uncle-blocks',
    };
    const txs: NavItem | null = {
      text: 'Transactions',
      nextRoute: { pathname: '/txs' as const },
      icon: 'transactions',
      isActive: pathname === '/txs' || pathname === '/tx/[hash]',
    };
    const pendingTxs: NavItem | null = {
      text: 'Pending Transactions',
      nextRoute: { pathname: '/pending-txs' as const },
      icon: 'transactions',
      isActive: pathname === '/pending-txs',
    };
    const internalTxs: NavItem | null = {
      text: 'Contract Internal Transactions',
      nextRoute: { pathname: '/internal-txs' as const },
      icon: 'internal_txns',
      isActive: pathname === '/internal-txs',
    };
    const userOps: NavItem | null = config.features.userOps.isEnabled ? {
      text: 'User operations',
      nextRoute: { pathname: '/ops' as const },
      icon: 'user_op',
      isActive: pathname === '/ops' || pathname === '/op/[hash]',
    } : null;
    const blobs: NavItem | null = {
      text: 'Blobs',
      nextRoute: { pathname: '/blobs' as const },
      icon: 'blob',
      isActive: pathname === '/blobs',
    };
    const aaTransactions: NavItem | null = {
      text: 'AA Transactions',
      nextRoute: { pathname: '/aa-transactions' as const },
      icon: 'user_op',
      isActive: pathname === '/aa-transactions',
    };
    const eip7702Authorizations: NavItem | null = {
      text: 'EIP-7702 Authorizations',
      nextRoute: { pathname: '/eip7702-authorizations' as const },
      icon: 'verified',
      isActive: pathname === '/eip7702-authorizations',
    };

    const verifiedContracts: NavItem | null =
     {
       text: 'Verified contracts',
       nextRoute: { pathname: '/verified-contracts' as const },
       icon: 'verified',
       isActive: pathname === '/verified-contracts',
     };
    const ensLookup = config.features.nameService.isEnabled ? {
      text: 'Name services lookup',
      nextRoute: { pathname: '/name-domains' as const },
      icon: 'ENS',
      isActive: pathname === '/name-domains' || pathname === '/name-domains/[name]',
    } : null;
    const validators = config.features.validators.isEnabled ? {
      text: 'Top validators',
      nextRoute: { pathname: '/validators' as const },
      icon: 'validator',
      isActive: pathname === '/validators' || pathname === '/validators/[id]',
    } : null;
    const rollupDeposits = {
      text: `Deposits (L1${ rightLineArrow }L2)`,
      nextRoute: { pathname: '/deposits' as const },
      icon: 'arrows/south-east',
      isActive: pathname === '/deposits',
    };
    const rollupWithdrawals = {
      text: `Withdrawals (L2${ rightLineArrow }L1)`,
      nextRoute: { pathname: '/withdrawals' as const },
      icon: 'arrows/north-east',
      isActive: pathname === '/withdrawals',
    };
    const rollupTxnBatches = {
      text: 'Txn batches',
      nextRoute: { pathname: '/batches' as const },
      icon: 'txn_batches',
      isActive: pathname === '/batches',
    };
    const rollupOutputRoots = {
      text: 'Output roots',
      nextRoute: { pathname: '/output-roots' as const },
      icon: 'output_roots',
      isActive: pathname === '/output-roots',
    };
    const rollupDisputeGames = config.features.faultProofSystem.isEnabled ? {
      text: 'Dispute games',
      nextRoute: { pathname: '/dispute-games' as const },
      icon: 'games',
      isActive: pathname === '/dispute-games',
    } : null;
    const mudWorlds = config.features.mudFramework.isEnabled ? {
      text: 'MUD worlds',
      nextRoute: { pathname: '/mud-worlds' as const },
      icon: 'MUD_menu',
      isActive: pathname === '/mud-worlds',
    } : null;

    const rollupFeature = config.features.rollup;

    const rollupInteropMessages = rollupFeature.isEnabled && rollupFeature.interopEnabled ? {
      text: 'Interop messages',
      nextRoute: { pathname: '/interop-messages' as const },
      icon: 'interop',
      isActive: pathname === '/interop-messages',
    } : null;

    if (rollupFeature.isEnabled && (
      rollupFeature.type === 'optimistic' ||
      rollupFeature.type === 'arbitrum' ||
      rollupFeature.type === 'zkEvm' ||
      rollupFeature.type === 'scroll'
    )) {
      blockchainNavItems = [
        [
          txs,
          internalTxs,
          rollupDeposits,
          rollupWithdrawals,
          rollupInteropMessages,
        ].filter(Boolean),
        [
          blocks,
          rollupTxnBatches,
          rollupDisputeGames,
          rollupFeature.outputRootsEnabled ? rollupOutputRoots : undefined,
        ].filter(Boolean),
        [
          userOps,
          topAccounts,
          mudWorlds,
          verifiedContracts,
          ensLookup,
        ].filter(Boolean),
      ];
    } else if (rollupFeature.isEnabled && rollupFeature.type === 'shibarium') {
      blockchainNavItems = [
        [
          txs,
          internalTxs,
          rollupDeposits,
          rollupWithdrawals,
        ],
        [
          blocks,
          userOps,
          topAccounts,
          verifiedContracts,
          ensLookup,
        ].filter(Boolean),
      ];
    } else if (rollupFeature.isEnabled && rollupFeature.type === 'zkSync') {
      blockchainNavItems = [
        [
          txs,
          internalTxs,
          userOps,
          blocks,
          rollupTxnBatches,
        ].filter(Boolean),
        [
          topAccounts,
          verifiedContracts,
          ensLookup,
        ].filter(Boolean),
      ];
    } else {
      // Grouped with horizontal dividers: Transactions/Blobs | AA & EIP-7702 | Blocks | Accounts/Contracts
      const beaconWithdrawals = config.features.beaconChain.isEnabled ? {
        text: 'Withdrawals',
        nextRoute: { pathname: '/withdrawals' as const },
        icon: 'arrows/north-east' as const,
        isActive: pathname === '/withdrawals',
      } : null;
      blockchainNavItems = [
        [ txs, pendingTxs, internalTxs, userOps, blobs, aaTransactions, eip7702Authorizations ].filter(Boolean),
        // [ aaTransactions, eip7702Authorizations ].filter(Boolean),
        [ blocks, forkedBlocks, uncleBlocks ].filter(Boolean),
        [ topAccounts, verifiedContracts, ensLookup, beaconWithdrawals ].filter(Boolean),
      ];
    }

    const tokensNavItems = [
      {
        text: `Top Tokens (${ tokenStandardLabel })`,
        nextRoute: { pathname: '/tokens' as const },
        icon: 'token',
        isActive: pathname === '/tokens' || pathname.startsWith('/token/'),
      },
      {
        text: `Token Transfers (${ tokenStandardLabel })`,
        nextRoute: { pathname: '/token-transfers' as const },
        icon: 'token-transfers',
        isActive: pathname === '/token-transfers',
      },
      {
        text: 'Token Flow Visualizer',
        nextRoute: { pathname: '/token-flow' as const },
        icon: 'token-transfers',
        isActive: pathname === '/token-flow',
      },
      config.features.pools.isEnabled && {
        text: 'DEX tracker',
        nextRoute: { pathname: '/pools' as const },
        icon: 'dex-tracker',
        isActive: pathname === '/pools' || pathname.startsWith('/pool/'),
      },
    ].filter(Boolean);

    // Developers menu items - Etherscan style (divider after API Documentation, after Contract Diff Checker)
    const developersNavItems: Array<Array<NavItem>> = [
      [
        {
          text: 'API Plans',
          nextRoute: { pathname: '/api-plans' as const },
          icon: 'restAPI',
          isActive: pathname === '/api-plans',
        },
        config.features.restApiDocs.isEnabled ? {
          text: 'API Documentation',
          nextRoute: { pathname: '/api-docs' as const },
          icon: 'docs',
          isActive: pathname === '/api-docs',
        } : null,
      ].filter(Boolean),
      [
        config.features.graphqlApiDocs.isEnabled ? {
          text: 'GraphQL',
          nextRoute: { pathname: '/graphiql' as const },
          icon: 'graphQL',
          isActive: pathname === '/graphiql',
        } : null,
        {
          text: 'Code Reader',
          nextRoute: { pathname: '/code-reader' as const },
          icon: 'code',
          isActive: pathname === '/code-reader',
        },
        {
          text: 'Verify Contract',
          nextRoute: { pathname: '/contract-verification' as const },
          icon: 'verified',
          isActive: pathname.startsWith('/contract-verification'),
        },
        {
          text: 'Similar Contract Search',
          nextRoute: { pathname: '/tools/similar-contracts' as const },
          icon: 'search',
          isActive: pathname === '/tools/similar-contracts',
        },
        {
          text: 'Smart Contract Search',
          nextRoute: { pathname: '/contract-search' as const },
          icon: 'search',
          isActive: pathname === '/contract-search',
        },
        {
          text: 'Contract Diff Checker',
          nextRoute: { pathname: '/contract-diff' as const },
          icon: 'diff',
          isActive: pathname === '/contract-diff',
        },
      ].filter(Boolean),
      [
        {
          text: 'Vyper Online Compiler',
          nextRoute: { pathname: '/tools/vyper' as const },
          icon: 'code',
          isActive: pathname === '/tools/vyper',
        },
        {
          text: 'Bytecode to Opcode',
          nextRoute: { pathname: '/tools/opcode' as const },
          icon: 'code',
          isActive: pathname === '/tools/opcode',
        },
        {
          text: 'Broadcast Transaction',
          nextRoute: { pathname: '/tools/broadcast-tx' as const },
          icon: 'transactions',
          isActive: pathname === '/tools/broadcast-tx',
        },
      ],
    ];

    // More menu items - Etherscan style with sections
    const moreNavItems: Array<NavItem> | Array<Array<NavItem>> = [
      // Tools section
      [
        {
          text: 'Input Data Decoder',
          nextRoute: { pathname: '/tools/input-decoder' as const },
          icon: 'blobs/text',
          isActive: pathname === '/tools/input-decoder',
        },
        {
          text: 'Unit Converter',
          nextRoute: { pathname: '/tools/unit-converter' as const },
          icon: 'token',
          isActive: pathname === '/tools/unit-converter',
        },
        {
          text: 'CSV Export',
          nextRoute: { pathname: '/tools/csv-export' as const },
          icon: 'files/csv',
          isActive: pathname === '/tools/csv-export',
        },
        {
          text: 'Account Balance Checker',
          nextRoute: { pathname: '/tools/balance-checker' as const },
          icon: 'wallet',
          isActive: pathname === '/tools/balance-checker',
        },
      ],
      // Explore section
      [
        config.features.gasTracker.isEnabled ? {
          text: 'Gas Tracker',
          nextRoute: { pathname: '/gas-tracker' as const },
          icon: 'gas',
          isActive: pathname.startsWith('/gas-tracker'),
        } : {
          text: 'Gas Tracker',
          url: '/gastracker',
          icon: 'gas',
        },
        {
          text: 'DEX Tracker',
          url: '/dex',
          icon: 'swap',
        },
        {
          text: 'Node Tracker',
          url: '/nodetracker',
          icon: 'validator',
        },
        {
          text: 'Label Cloud',
          url: '/labelcloud',
          icon: 'publictags',
        },
        {
          text: 'Domain Name Lookup',
          url: '/name-lookup',
          icon: 'search',
        },
      ],
      // Services section
      [
        {
          text: 'Token Approvals',
          nextRoute: { pathname: '/tools/token-approvals' as const },
          icon: 'token',
          isActive: pathname === '/tools/token-approvals',
        },
        {
          text: 'Verified Signature',
          nextRoute: { pathname: '/verifiedSignatures' as const },
          icon: 'verified',
          isActive: pathname === '/verifiedSignatures',
        },
        {
          text: 'Input Data Messages (IDM)',
          nextRoute: { pathname: '/tools/input-decoder' as const },
          icon: 'email',
          isActive: pathname === '/tools/input-decoder',
        },
        {
          text: 'Advanced Filter',
          url: '/advanced-filter',
          icon: 'filter',
        },
        {
          text: 'Community',
          url: '/',
          icon: 'link_external',
        },
        // {
        //   text: 'Priority Support',
        //   nextRoute: { pathname: '/priority-support' as const },
        //   icon: 'star_outline',
        //   isActive: pathname === '/priority-support',
        // },
      ],
    ].filter(arr => Array.isArray(arr) ? arr.length > 0 : Boolean(arr));

    // Etherscan-style navigation
    const mainNavItems: ReturnType['mainNavItems'] = [
      // Home link
      {
        text: 'Home',
        nextRoute: { pathname: '/' as const },
        icon: 'home',
        isActive: pathname === '/',
      },
      // Blockchain dropdown
      {
        text: 'Blockchain',
        icon: 'globe-b',
        isActive: blockchainNavItems.flat().some(item => isInternalItem(item) && item.isActive),
        subItems: blockchainNavItems,
      },
      // Validators dropdown (BscScan-style: Leaderboard + Set Info)
      {
        text: 'Validators',
        icon: 'validator',
        isActive: pathname === '/validators' || pathname.startsWith('/validators/') || pathname === '/validatorset' || pathname === '/validatorsgroup',
        subItems: [
          {
            text: 'Validators Leaderboard',
            nextRoute: { pathname: '/validators' as const },
            icon: 'validator',
            isActive: pathname === '/validators' || pathname.startsWith('/validators/'),
          },
          {
            text: 'View Validators Set Info',
            nextRoute: { pathname: '/validatorset' as const },
            icon: 'validator',
            isActive: pathname === '/validatorset',
          },
          {
            text: 'Group By Validators',
            nextRoute: { pathname: '/validatorsgroup' as const },
            icon: 'validator',
            isActive: pathname === '/validatorsgroup',
          },
        ],
      },
      // Tokens dropdown
      {
        text: 'Tokens',
        icon: 'token',
        isActive: tokensNavItems.flat().some(item => isInternalItem(item) && item.isActive),
        subItems: tokensNavItems,
      },
      // NFTs dropdown (BscScan-style: Top NFTs, Top Mints, Latest Trades, Latest Transfers, Latest Mints)
      {
        text: 'NFTs',
        icon: 'nft',
        isActive: pathname.startsWith('/nft-'),
        subItems: [
          {
            text: 'Top NFTs',
            nextRoute: { pathname: '/nft-top-contracts' as const },
            icon: 'nft',
            isActive: pathname === '/nft-top-contracts',
          },
          {
            text: 'Top Mints',
            nextRoute: { pathname: '/nft-top-mints' as const },
            icon: 'token-transfers',
            isActive: pathname === '/nft-top-mints',
          },
          {
            text: 'Latest Trades',
            nextRoute: { pathname: '/nft-trades' as const },
            icon: 'token-transfers',
            isActive: pathname === '/nft-trades',
          },
          {
            text: 'Latest Transfers',
            nextRoute: { pathname: '/nft-transfers' as const },
            icon: 'token-transfers',
            isActive: pathname === '/nft-transfers',
          },
          {
            text: 'Latest Mints',
            nextRoute: { pathname: '/nft-latest-mints' as const },
            icon: 'token-transfers',
            isActive: pathname === '/nft-latest-mints',
          },
        ],
      },
      // DApps link (if marketplace enabled)
      config.features.marketplace.isEnabled ? {
        text: 'DApps',
        nextRoute: { pathname: '/apps' as const },
        icon: 'apps',
        isActive: pathname.startsWith('/app'),
      } : null,
      // Resources dropdown - Etherscan style (divider after Leaderboard)
      {
        text: 'Resources',
        icon: 'stats',
        isActive: pathname.startsWith('/charts-stats') || pathname.startsWith('/top-statistics') || pathname.startsWith('/leaderboard'),
        subItems: [
          [
            {
              text: 'Charts And Stats',
              nextRoute: { pathname: '/charts-stats' as const },
              icon: 'stats',
              isActive: pathname === '/charts-stats',
            },
            {
              text: 'Top Statistics',
              nextRoute: { pathname: '/top-statistics' as const },
              icon: 'top-accounts',
              isActive: pathname === '/top-statistics',
            },
            {
              text: 'Leaderboard',
              nextRoute: { pathname: '/leaderboard' as const },
              icon: 'star_outline',
              isActive: pathname === '/leaderboard',
            },
          ],
          [
            {
              text: 'Directory',
              url: 'https://etherscan.io/directory',
              icon: 'apps',
            },
            {
              text: 'Newsletter',
              url: 'https://etherscan.io/newsletters',
              icon: 'email',
            },
            {
              text: 'Knowledge Base',
              url: 'https://info.etherscan.com/',
              icon: 'docs',
            },
          ],
        ],
      },
      // Developers dropdown - Etherscan style
      {
        text: 'Developers',
        icon: 'restAPI',
        isActive: developersNavItems.flat().some(item => isInternalItem(item) && item.isActive),
        subItems: developersNavItems,
      },
      // More dropdown - Etherscan style mega menu
      {
        text: 'More',
        icon: 'gear',
        isActive: moreNavItems.flat().some(item => isInternalItem(item) && item.isActive),
        subItems: moreNavItems,
      },
    ].filter(Boolean);

    const accountNavItems: ReturnType['accountNavItems'] = [
      {
        text: 'Watch list',
        nextRoute: { pathname: '/account/watchlist' as const },
        icon: 'watchlist',
        isActive: pathname === '/account/watchlist',
      },
      {
        text: 'Private tags',
        nextRoute: { pathname: '/account/tag-address' as const },
        icon: 'privattags',
        isActive: pathname === '/account/tag-address',
      },
      {
        text: 'API keys',
        nextRoute: { pathname: '/account/api-key' as const },
        icon: 'API',
        isActive: pathname === '/account/api-key',
      },
      {
        text: 'Custom ABI',
        nextRoute: { pathname: '/account/custom-abi' as const },
        icon: 'ABI',
        isActive: pathname === '/account/custom-abi',
      },
      config.features.addressVerification.isEnabled && {
        text: 'Verified addrs',
        nextRoute: { pathname: '/account/verified-addresses' as const },
        icon: 'verified',
        isActive: pathname === '/account/verified-addresses',
      },
    ].filter(Boolean);

    return { mainNavItems, accountNavItems };
  }, [ pathname, tokenStandardLabel ]);
}